import type { RxJsonSchema } from 'rxdb'

/**
 * EntityLink Document Type (universal link/reference between any two entities)
 *
 * Supports backlinks: given a targetEntityId, you can find all entities that reference it.
 *
 * Entity types use backend constants: 'work_item', 'document', 'project'
 * linkType: 0 = inline (from editor [[ mention]), 1 = explicit (from sidebar)
 */
export interface EntityLinkDocument {
  id: string
  envId: string

  sourceEntityType: string // 'work_item' | 'document' | 'project'
  sourceEntityId: string
  targetEntityType: string // 'work_item' | 'document' | 'project'
  targetEntityId: string

  linkType: number // 0 = inline, 1 = explicit

  // Denormalized for offline display
  sourceEntityName?: string
  sourceEntityIcon?: string | null
  targetEntityName?: string
  targetEntityIcon?: string | null

  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * Link type constants
 */
export const LINK_TYPES = {
  INLINE: 0,
  EXPLICIT: 1,
} as const

export type LinkType = (typeof LINK_TYPES)[keyof typeof LINK_TYPES]

/**
 * RxDB Schema for EntityLink
 */
export const entityLinkSchema: RxJsonSchema<EntityLinkDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    sourceEntityType: {
      type: 'string',
      maxLength: 50,
    },
    sourceEntityId: {
      type: 'string',
      maxLength: 36,
    },
    targetEntityType: {
      type: 'string',
      maxLength: 50,
    },
    targetEntityId: {
      type: 'string',
      maxLength: 36,
    },
    linkType: {
      type: 'number',
      minimum: 0,
      maximum: 1,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Denormalized fields
    sourceEntityName: {
      type: 'string',
      maxLength: 500,
      default: '',
    },
    sourceEntityIcon: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    targetEntityName: {
      type: 'string',
      maxLength: 500,
      default: '',
    },
    targetEntityIcon: {
      type: ['string', 'null'],
      maxLength: 50,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'sourceEntityType',
    'sourceEntityId',
    'targetEntityType',
    'targetEntityId',
    'linkType',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'sourceEntityId',
    'targetEntityId',
    'envId',
    'updatedOn',
    ['sourceEntityId', 'sourceEntityType', 'isDeleted'],
    ['targetEntityId', 'targetEntityType', 'isDeleted'],
  ],
}
